import React, { useState, useEffect } from 'react';
import Api from 'utils/api';
import styles from './MachineInterfaceTab.module.css';

const PROTOCOL_TYPES = ['MDB', 'PULSE', 'SERIAL'];

const MachineInterfaceTab = () => {
  const [interfaces, setInterfaces] = useState([]);
  const [loading, setLoading] = useState(true);

  // 모달 및 폼 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  
  const initialFormState = {
    machineInterfaceId: null,
    machineInterfaceName: '',
    protocolType: 'MDB',
    configList: [{ configKey: '', configValue: '' }] 
  };
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    fetchInterfaceList();
  }, []);

  const fetchInterfaceList = async () => {
    setLoading(true);
    try {
      const data = await Api.get('/machine/interface');
      setInterfaces(data?.machineInterfaceList || []);
    } catch (error) {
      console.error("목록 로드 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode, data = null) => {
    setIsEditMode(mode === 'edit');
    if (data) {
      const rawConfigs = data.configs || data.configList || [];
      const mappedConfigs = rawConfigs.map(c => ({
        machineInterfaceConfigId: c.machineInterfaceConfigId || null,
        configKey: c.configKey || '',
        configValue: c.configValue || ''
      }));

      setFormData({
        machineInterfaceId: data.machineInterfaceId,
        machineInterfaceName: data.machineInterfaceName,
        protocolType: data.protocolType || 'MDB',
        configList: mappedConfigs.length > 0 ? mappedConfigs : [{ configKey: '', configValue: '' }]
      });
    } else {
      setFormData(initialFormState);
    }
    setIsModalOpen(true);
  };

  const handleAddConfig = () => {
    setFormData({
      ...formData,
      configList: [...formData.configList, { configKey: '', configValue: '' }]
    });
  };

  const handleRemoveConfig = (index) => {
    const newList = formData.configList.filter((_, i) => i !== index);
    setFormData({ ...formData, configList: newList });
  };

  const handleConfigChange = (index, field, value) => {
    const newList = [...formData.configList];
    newList[index][field] = value;
    setFormData({ ...formData, configList: newList });
  };

  const handleSave = async () => {
    const { machineInterfaceName, protocolType, configList } = formData;
    if (!machineInterfaceName.trim()) return alert("인터페이스 이름을 입력해주세요.");
    
    const filteredConfigs = configList.filter(c => c.configKey.trim() && c.configValue.trim());

    try {
      const requestBody = {
        machineInterfaceName,
        protocolType,
        configList: filteredConfigs,
      };

      let response;
      if (isEditMode) {
        requestBody.machineInterfaceId = formData.machineInterfaceId;
        response = await Api.patch('/machine/interface', requestBody);
      } else {
        response = await Api.post('/machine/interface', requestBody);
      }

      if (response) {
        alert(isEditMode ? "수정되었습니다." : "등록되었습니다.");
        setIsModalOpen(false);
        fetchInterfaceList();
      }
    } catch (error) {
      alert("저장 중 오류가 발생했습니다.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    const data = await Api.delete(`/machine/interface?machineInterfaceId=${id}`);
    if (data.code === 200) {
      alert("삭제되었습니다.");
      fetchInterfaceList();
    }
  };

  if (loading) return <div className={styles.centerMsg}>로딩 중...</div>;

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div className={styles.countInfo}>인터페이스 <span className={styles.countNumber}>{interfaces.length}</span></div>
        <button className={styles.addBtn} onClick={() => handleOpenModal('add')}>+ 등록</button>
      </div>

      <div className={styles.cardGrid}>
        {interfaces.map((item) => (
          <div key={item.machineInterfaceId} className={styles.interfaceCard}>
            <div className={styles.cardHeader}>
              <h3 className={styles.interfaceName}>{item.machineInterfaceName}</h3>
              <span className={styles.protocolBadge}>{item.protocolType}</span>
            </div>
            
            {/* ✅ 카드 내부 상세 설정 표시 영역 추가 */}
            <div className={styles.cardBody}>
              <ul className={styles.configSummary}>
                {(item.configs || item.configList || []).map((cfg, idx) => (
                  <li key={idx} className={styles.configItem}>
                    <span className={styles.cfgKey}>{cfg.configKey}:</span>
                    <span className={styles.cfgValue}>{cfg.configValue}</span>
                  </li>
                ))}
                {(!item.configs && !item.configList) && <li className={styles.noConfig}>설정 없음</li>}
              </ul>
            </div>

            <div className={styles.cardFooter}>
              <button className={styles.editBtn} onClick={() => handleOpenModal('edit', item)}>수정</button>
              <button className={styles.deleteBtn} onClick={() => handleDelete(item.machineInterfaceId)}>삭제</button>
            </div>
          </div>
        ))}
      </div>

      {/* 모달 영역 생략 (기존과 동일) */}
      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>{isEditMode ? '인터페이스 수정' : '새 인터페이스 등록'}</h3>
              <button onClick={() => setIsModalOpen(false)}>✕</button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.inputGroup}>
                <label>인터페이스 명칭</label>
                <input
                  value={formData.machineInterfaceName}
                  onChange={(e) => setFormData({...formData, machineInterfaceName: e.target.value})}
                  placeholder="예: 메인 키오스크 MDB"
                />
              </div>

              <div className={styles.inputGroup}>
                <label>프로토콜 타입</label>
                <select 
                  value={formData.protocolType}
                  onChange={(e) => setFormData({...formData, protocolType: e.target.value})}
                  className={styles.modalSelect}
                >
                  {PROTOCOL_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className={styles.configSection}>
                <div className={styles.configHeader}>
                  <label>상세 설정 (Config)</label>
                  <button type="button" onClick={handleAddConfig} className={styles.smallAddBtn}>+ 추가</button>
                </div>
                {formData.configList.map((config, idx) => (
                  <div key={idx} className={styles.configRow}>
                    <input 
                      placeholder="Key" 
                      value={config.configKey} 
                      onChange={(e) => handleConfigChange(idx, 'configKey', e.target.value)} 
                    />
                    <input 
                      placeholder="Value" 
                      value={config.configValue} 
                      onChange={(e) => handleConfigChange(idx, 'configValue', e.target.value)} 
                    />
                    {formData.configList.length > 1 && (
                      <button onClick={() => handleRemoveConfig(idx)} className={styles.removeBtn}>✕</button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setIsModalOpen(false)}>취소</button>
              <button className={styles.saveBtn} onClick={handleSave}>저장하기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MachineInterfaceTab;